import { NavLink, Outlet, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, Calendar, Image as ImageIcon, Users, LifeBuoy, UserCog, LogOut, ExternalLink, Home, Key, BookOpen } from "lucide-react";

const navItems = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/events", label: "Events", icon: Calendar },
  { to: "/admin/gallery", label: "Gallery", icon: ImageIcon },
  { to: "/admin/leaders", label: "Leaders", icon: Users },
  { to: "/admin/resources", label: "Resources", icon: LifeBuoy },
  { to: "/admin/home-content", label: "Home Copy", icon: Home },
  { to: "/admin/users", label: "Admin Users", icon: UserCog },
  { to: "/admin/account", label: "My Account", icon: Key },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-[var(--rts-blue-pale)]/40 flex flex-col lg:flex-row" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="lg:w-64 lg:min-h-screen bg-[var(--rts-ink)] text-white lg:sticky lg:top-0 flex flex-col">
        <div className="p-5 border-b border-white/10 flex items-center gap-3">
          <img src="/logo.jpg" alt="PARS" className="h-10 w-auto object-contain rounded" />
          <div className="leading-tight">
            <div className="font-display font-bold text-sm">PARS Admin</div>
            <div className="text-[10px] uppercase tracking-widest text-white/50">Content Manager</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-x-auto lg:overflow-visible flex lg:block" data-testid="admin-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-semibold transition whitespace-nowrap ${
                  isActive
                    ? "bg-[var(--rts-blue)] text-white"
                    : "text-white/75 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-2">
          <a href="/admin-guide.pdf" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-white/70 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg px-3 py-2 min-h-[40px]" data-testid="admin-guide-link">
            <BookOpen className="w-4 h-4" /> Download Admin Guide (PDF)
          </a>
          <Link to="/" target="_blank" className="flex items-center gap-2 text-xs text-white/60 hover:text-white/90 px-3">
            <ExternalLink className="w-3 h-3" /> View public site
          </Link>
          {user && (
            <div className="text-xs text-white/60 truncate" data-testid="admin-current-user">
              {user.email}
            </div>
          )}
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="w-full inline-flex items-center gap-2 justify-center bg-white/5 hover:bg-[var(--rts-red)] text-white text-sm font-bold py-2.5 rounded-lg transition min-h-[44px]"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 p-4 sm:p-8 max-w-6xl mx-auto w-full">
        <Outlet />
      </main>
    </div>
  );
}
